import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16, default as _styled17, default as _styled18, default as _styled19, default as _styled20, default as _styled21 } from "styled-components";
import React from "react";
import dynamic from "next/dynamic";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { Seo } from "~/features/seo";
import { takeFirst } from "~/features/app/utils";
import { AppHeader } from "~/features/app/app-header-v2";
import { AppFooter } from "~/features/app/app-footer";
import { NavLinks } from "~/features/app/app-header-v2/nav-links";
import { NavLogo } from "~/features/app/app-header-v2/nav-logo";
import { NavHamburger } from "~/features/app/app-header-v2/nav-hamburger";
import { NavAdditionalLinks } from "~/features/app/app-header-v2/nav-additional-links";
import { NavLayoutProvider, type NavTheme } from "~/features/app/app-header-v2/context";
import { AdditionalDrawerNavigation } from "~/features/app/app-header-v2/nav-drawer/additional-nav";
import { FooterAdditionalLinks } from "~/features/app/app-footer/footer-additional-links";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { useLinkIcon } from "~/features/ui/link/use-link-icon";
import { Newsletter } from "~/features/newsletter";
import Flag from "~/public/usa-flag.svg";
import { FooterLinks } from "~/features/app/app-footer/footer-links";
import { FooterSocials } from "~/features/app/app-footer/footer-socials";
import { FooterBottomLinks } from "~/features/app/app-footer/footer-bottom-links";
import { Pattern, type PatternType } from "~/features/app/pattern";
import { AnnouncementBar } from "~/features/ui/announcement-bar";
import { isLinkValid } from "~/features/ui/link/utils";
import { useRecaptchaVisibility } from "~/features/re-captcha/use-re-captcha-visibility";
import { NavDrawerProvider } from "~/features/app/app-header-v2/nav-drawer/context";
import { useRequestQuoteGlobals } from "~/features/request-quote-dialog/use-request-quote-globals";
import { REQUEST_QUOTE_REV7_MODAL_ID, REQUEST_QUOTE_REV8_MODAL_ID } from "~/features/ui/link";
import { LandingHeader } from "~/features/app/landing-header";
import { NavLinks as LandingNavLinks } from "~/features/app/landing-header/nav-links";
import { NavLogo as LandingNavLogo } from "~/features/app/landing-header/nav-logo";
import { NavHamburger as LandingNavHamburger } from "~/features/app/landing-header/nav-hamburger";
import { NavAdditionalLinks as LandingNavAdditionalLinks, type fragment as landingNavAdditionalLinksFragment } from "~/features/app/landing-header/nav-additional-links";
import { AdditionalDrawerNavigation as LandingAdditionalDrawerNavigation } from "~/features/app/landing-header/nav-drawer/additional-nav";
import { LandingFooter } from "~/features/app/landing-footer";
import { ExtraNavLogo } from "~/features/app/app-header-v2/extra-nav-logo";
import { type fragment as assetFragment } from "~/features/craft/asset";
import { type fragment as linkFragment } from "~/features/ui/link/utils";
import { FooterAdditionalLinks as LandingFooterAdditionalLinks } from "~/features/app/landing-footer/footer-additional-links";
import { FooterLinks as LandingFooterLinks } from "~/features/app/landing-footer/footer-links";
import { FooterSocials as LandingFooterSocials } from "~/features/app/landing-footer/footer-socials";
import { FooterBottomLinks as LandingFooterBottomLinks } from "~/features/app/landing-footer/footer-bottom-links";
const NavDrawer = dynamic(() => import("~/features/app/app-header-v2/nav-drawer").then(mod => mod.NavDrawer), {
  ssr: false
});
const LandingNavDrawer = dynamic(() => import("~/features/app/landing-header/nav-drawer").then(mod => mod.NavDrawer), {
  ssr: false
});
const RequestQuoteDialog = dynamic(() => import("~/features/request-quote-dialog").then(mod => mod.RequestQuoteDialog), {
  ssr: false
});
const fragment = graphql(`
  fragment GlobalCommon on common_GlobalSet {
    seo {
      ...Seo
    }
    navLinks {
      ...NavLinksV2
      ...LandingNavLinks
    }
    navAdditionalLinks {
      ...NavAdditionalLinksV2
      ...LandingNavAdditionalLinks
    }
    navDrawerLinks {
      ...NavDrawerLinksV2
      ...LandingNavDrawerLinks
    }
    navDrawerAdditionalLinks {
      ...NavDrawerAdditionalLinks
      ...LandingNavDrawerAdditionalLinks
    }
    footerLink: baseLink {
      ...Link
    }
    footerAdditionalLinks {
      ...FooterAdditionalLinks
      ...LandingFooterAdditionalLinks
    }
    footerLinks {
      ...FooterLinks
      ...LandingFooterLinks
    }
    footerSocials {
      ...FooterSocials
      ...LandingFooterSocials
    }
    footerBottomLinks {
      ...FooterBottomLinks
      ...LandingFooterBottomLinks
    }
    extraNavLogo: baseImage {
      ...Asset
    }
    extraNavLogoTransparent: baseImage2 {
      ...Asset
    }
    extraNavLogoLink: baseLink2 {
      ...Link
    }
    newsletterText: baseText
    announcementBarText: baseRichText
  }
`);
export type PageMode = "default" | "landing";
type Props = {
  common?: FragmentType<typeof fragment>;
  navTheme: NavTheme;
  pattern: PatternType;
  footerPattern?: PatternType;
  pageMode?: PageMode;
  /** Full-viewport heroes (e.g. Cube Experience) — omit footer so the page does not scroll past 100vh. */
  hideFooter?: boolean;
  customLogo?: FragmentType<typeof assetFragment>;
  pageNavAdditionalLinks?: FragmentType<typeof landingNavAdditionalLinksFragment>[];
  supportLink?: FragmentType<typeof linkFragment>;
  supportLinkVariant?: string;
  backButton?: boolean;
  /** Page mode: light chrome on header + footers. */
  isPageHighlighted?: boolean;
  children: React.ReactNode;
};
export function AppLayout(props: Props) {
  const {
    children,
    navTheme,
    pattern,
    footerPattern,
    common,
    pageMode = "default",
    hideFooter,
    customLogo,
    pageNavAdditionalLinks,
    supportLink,
    supportLinkVariant,
    backButton,
    isPageHighlighted = false
  } = props;
  const requestQuote = useRequestQuoteGlobals();
  const requestQuoteData = requestQuote.status === "ready" ? requestQuote.data : null;
  const requestQuoteLoadProps = {
    loadStatus: requestQuote.status,
    loadMessage: requestQuote.message,
    onRetry: requestQuote.retry
  };
  const {
    seo,
    navLinks = [],
    navAdditionalLinks = [],
    navDrawerLinks = [],
    navDrawerAdditionalLinks = [],
    footerLink,
    footerLinks = [],
    footerAdditionalLinks = [],
    footerSocials = [],
    footerBottomLinks = [],
    extraNavLogo: globalExtraNavLogo = [],
    extraNavLogoTransparent: globalExtraNavLogoTransparent = [],
    extraNavLogoLink: globalExtraNavLogoLink = [],
    newsletterText,
    announcementBarText
  } = useFragment(fragment, common) || {};
  useRecaptchaVisibility();
  const footerCta = takeFirst(footerLink);
  const icon = useLinkIcon(footerCta);
  const resolvedExtraNavLogo = takeFirst(globalExtraNavLogo);
  const resolvedExtraNavLogoTransparent = takeFirst(globalExtraNavLogoTransparent);
  const resolvedExtraNavLogoLink = takeFirst(globalExtraNavLogoLink);
  const hasExtraNavLogo = Boolean(resolvedExtraNavLogo || resolvedExtraNavLogoTransparent);
  const isLanding = pageMode === "landing";
  const isHeaderHighlighted = isPageHighlighted && navTheme !== "transparent";
  if (isLanding) {
    return <>
        <Seo seo={takeFirst(seo)} />
        <Pattern pattern={pattern} />
        <NavLayoutProvider key={navTheme} theme={navTheme} isPageHighlighted={isHeaderHighlighted}>
          {announcementBarText && <AnnouncementBar text={announcementBarText} />}
          <NavDrawerProvider>
            <LandingHeader left={<_StyledDiv>
                  <LandingNavLogo customLogo={customLogo} />
                </_StyledDiv>} center={hasExtraNavLogo ? <_StyledDiv2>
                    <_StyledDiv3>
                      <ExtraNavLogo extraNavLogo={resolvedExtraNavLogo} extraNavLogoTransparent={resolvedExtraNavLogoTransparent} extraNavLogoLink={resolvedExtraNavLogoLink} layout="landing" />
                    </_StyledDiv3>
                    <_StyledDiv4>
                      <LandingNavLinks navLinks={navLinks as any} />
                    </_StyledDiv4>
                  </_StyledDiv2> : <_StyledDiv5>
                    <LandingNavLinks navLinks={navLinks as any} />
                  </_StyledDiv5>} right={<>
                  <_StyledDiv6>
                    <LandingNavAdditionalLinks navLinks={pageNavAdditionalLinks as any} backButton={backButton} />
                  </_StyledDiv6>
                  <_StyledLandingNavHamburger supportLink={supportLink} supportLinkVariant={supportLinkVariant} />
                </>} />
            <LandingNavDrawer navLinks={navDrawerLinks as any} additionalNav={<LandingAdditionalDrawerNavigation navLinks={navDrawerAdditionalLinks as any} />} />
            <main>{children}</main>
          </NavDrawerProvider>
          {!hideFooter && <LandingFooter isPageHighlighted={isPageHighlighted} footerPattern={footerPattern} link={isLinkValid(footerCta) ? <_StyledDiv7>
                    <_StyledFlag />
                    <Button variant="underline" as={Link} {...footerCta} icon={icon} />
                  </_StyledDiv7> : undefined} additionalLinks={<LandingFooterAdditionalLinks links={footerAdditionalLinks as any} />} socials={<LandingFooterSocials socials={footerSocials as any} />} newsletter={<_StyledDiv8>
                  <Newsletter text={newsletterText} />
                </_StyledDiv8>} links={<LandingFooterLinks groups={footerLinks as any} />} bottomLinks={<LandingFooterBottomLinks links={footerBottomLinks as any} />} />}
        </NavLayoutProvider>

        <RequestQuoteDialog data={requestQuoteData?.rev7} modalId={REQUEST_QUOTE_REV7_MODAL_ID} {...requestQuoteLoadProps} />
        <RequestQuoteDialog data={requestQuoteData?.rev8} modalId={REQUEST_QUOTE_REV8_MODAL_ID} legacyModalIds={["request-quote"]} {...requestQuoteLoadProps} showRev8Logo />
      </>;
  }
  return <>
      <Seo seo={takeFirst(seo)} />
      <Pattern pattern={pattern} />
      <NavLayoutProvider key={navTheme} theme={navTheme} isPageHighlighted={isHeaderHighlighted}>
        {announcementBarText && <AnnouncementBar text={announcementBarText} />}
        <NavDrawerProvider>
          <AppHeader left={<_StyledDiv9>
                <NavLogo />
              </_StyledDiv9>} center={hasExtraNavLogo ? <_StyledDiv10>
                  <_StyledDiv11>
                    <ExtraNavLogo extraNavLogo={resolvedExtraNavLogo} extraNavLogoTransparent={resolvedExtraNavLogoTransparent} extraNavLogoLink={resolvedExtraNavLogoLink} layout="app" />
                  </_StyledDiv11>
                  <_StyledDiv12>
                    <NavLinks navLinks={navLinks} />
                  </_StyledDiv12>
                </_StyledDiv10> : <_StyledDiv13>
                  <NavLinks navLinks={navLinks} />
                </_StyledDiv13>} right={<_StyledDiv14>
                <_StyledDiv15>
                  <NavAdditionalLinks navLinks={navAdditionalLinks} />
                </_StyledDiv15>
                <_StyledNavHamburger />
              </_StyledDiv14>} />
          <NavDrawer navLinks={navDrawerLinks} additionalNav={<AdditionalDrawerNavigation navLinks={navDrawerAdditionalLinks} />} />
          <main>{children}</main>
        </NavDrawerProvider>
        {!hideFooter && <AppFooter isPageHighlighted={isPageHighlighted} footerPattern={footerPattern} link={isLinkValid(footerCta) ? <_StyledDiv16>
                  <_StyledFlag2 />
                  <Button variant="underline" as={Link} {...footerCta} icon={icon} />
                </_StyledDiv16> : undefined} additionalLinks={<FooterAdditionalLinks links={footerAdditionalLinks} />} socials={<FooterSocials socials={footerSocials} />} newsletter={<_StyledDiv17>
                <Newsletter text={newsletterText} />
              </_StyledDiv17>} links={<FooterLinks groups={footerLinks} />} bottomLinks={<FooterBottomLinks links={footerBottomLinks} />} />}
      </NavLayoutProvider>

      <RequestQuoteDialog data={requestQuoteData?.rev7} modalId={REQUEST_QUOTE_REV7_MODAL_ID} {...requestQuoteLoadProps} />
      <RequestQuoteDialog data={requestQuoteData?.rev8} modalId={REQUEST_QUOTE_REV8_MODAL_ID} legacyModalIds={["request-quote"]} {...requestQuoteLoadProps} showRev8Logo />
    </>;
}
var _StyledDiv = _styled("div").withConfig({
  componentId: "sc-sf-5yimhz-0"
})({
  "position": "relative"
});
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-5yimhz-1"
})({
  "display": "flex",
  "minWidth": "0",
  "flex": "1 1 0%",
  "alignItems": "stretch"
});
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-5yimhz-2"
})({
  "display": "flex",
  "minWidth": "0",
  "flex": "1 1 0%",
  "justifyContent": "flex-end",
  "@media (min-width: 1280px)": {
    "flex": "none",
    "justifyContent": "flex-start"
  }
});
var _StyledDiv4 = _styled4("div").withConfig({
  componentId: "sc-sf-5yimhz-3"
})({
  "display": "none",
  "minWidth": "0",
  "flex": "1 1 0%",
  "@media (min-width: 1280px)": {
    "display": "block"
  }
});
var _StyledDiv5 = _styled5("div").withConfig({
  componentId: "sc-sf-5yimhz-4"
})({
  "display": "none",
  "flex": "1 1 0%",
  "@media (min-width: 1280px)": {
    "display": "block"
  }
});
var _StyledDiv6 = _styled6("div").withConfig({
  componentId: "sc-sf-5yimhz-5"
})({
  "display": "none",
  "alignItems": "stretch",
  "@media (min-width: 1280px)": {
    "display": "flex"
  }
});
var _StyledLandingNavHamburger = _styled7(LandingNavHamburger).withConfig({
  componentId: "sc-sf-5yimhz-6"
})({
  "height": "var(--header-height)"
});
var _StyledDiv7 = _styled8("div").withConfig({
  componentId: "sc-sf-5yimhz-7"
})({
  "display": "flex",
  "alignItems": "center",
  "columnGap": "1.6rem"
});
var _StyledFlag = _styled9(Flag).withConfig({
  componentId: "sc-sf-5yimhz-8"
})({
  "width": "3rem",
  "@media (min-width: 1024px)": {
    "width": "4rem"
  }
});
var _StyledDiv8 = _styled10("div").withConfig({
  componentId: "sc-sf-5yimhz-9"
})({
  "display": "flex",
  "alignItems": "center",
  "paddingLeft": "1.6rem",
  "paddingRight": "1.6rem",
  "paddingTop": "4rem",
  "paddingBottom": "4rem",
  "@media (min-width: 1024px)": {
    "padding": "8rem"
  }
});
var _StyledDiv9 = _styled11("div").withConfig({
  componentId: "sc-sf-5yimhz-10"
})({
  "position": "relative"
});
var _StyledDiv10 = _styled12("div").withConfig({
  componentId: "sc-sf-5yimhz-11"
})({
  "display": "flex",
  "minWidth": "0",
  "flex": "1 1 0%",
  "alignItems": "stretch"
});
var _StyledDiv11 = _styled13("div").withConfig({
  componentId: "sc-sf-5yimhz-12"
})({
  "display": "flex",
  "minWidth": "0",
  "flex": "1 1 0%",
  "justifyContent": "flex-end",
  "@media (min-width: 1280px)": {
    "flex": "none",
    "justifyContent": "flex-start"
  }
});
var _StyledDiv12 = _styled14("div").withConfig({
  componentId: "sc-sf-5yimhz-13"
})({
  "display": "none",
  "minWidth": "0",
  "flex": "1 1 0%",
  "@media (min-width: 1280px)": {
    "display": "block"
  }
});
var _StyledDiv13 = _styled15("div").withConfig({
  componentId: "sc-sf-5yimhz-14"
})({
  "display": "none",
  "flex": "1 1 0%",
  "@media (min-width: 1280px)": {
    "display": "block"
  }
});
var _StyledDiv14 = _styled16("div").withConfig({
  componentId: "sc-sf-5yimhz-15"
})({
  "position": "absolute",
  "right": "0",
  "top": "0",
  "display": "flex"
});
var _StyledDiv15 = _styled17("div").withConfig({
  componentId: "sc-sf-5yimhz-16"
})({
  "display": "none",
  "alignItems": "stretch",
  "@media (min-width: 1280px)": {
    "display": "flex"
  }
});
var _StyledNavHamburger = _styled18(NavHamburger).withConfig({
  componentId: "sc-sf-5yimhz-17"
})({
  "zIndex": "101",
  "height": "var(--header-height)"
});
var _StyledDiv16 = _styled19("div").withConfig({
  componentId: "sc-sf-5yimhz-18"
})({
  "display": "flex",
  "alignItems": "center",
  "columnGap": "1.6rem"
});
var _StyledFlag2 = _styled20(Flag).withConfig({
  componentId: "sc-sf-5yimhz-19"
})({
  "width": "3rem",
  "@media (min-width: 1024px)": {
    "width": "4rem"
  }
});
var _StyledDiv17 = /*#__PURE__*/_styled21("div").withConfig({
  displayName: "app-layout___StyledDiv17",
  componentId: "sc-1hm4ool-0"
})({
  "display": "flex",
  "alignItems": "center",
  "paddingLeft": "1.6rem",
  "paddingRight": "1.6rem",
  "paddingTop": "4rem",
  "paddingBottom": "4rem",
  "@media (min-width: 1024px)": {
    "padding": "8rem"
  }
});